import { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Search,
  Plus,
  Filter,
  MoreHorizontal,
  Eye,
  Edit,
  Download,
  Building2,
  Percent,
  Settings,
  Trash2,
} from 'lucide-react';
import { mockBanks } from '../../data/mockData';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { getBanks, createBank, updateBank, getBank, deleteBank } from '../../lib/api';

const typeColors = {
  Bank: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  NBFC: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
};

const statusColors = {
  Active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
  Inactive: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
};

const formatCurrency = (amount) => {
  if (amount >= 10000000) {
    return `₹${(amount / 10000000).toFixed(0)} Cr`;
  } else if (amount >= 100000) {
    return `₹${(amount / 100000).toFixed(0)} L`;
  }
  return `₹${amount.toLocaleString()}`;
};

const hasInvalidLoanAmountRange = (minLoanAmount, maxLoanAmount) => {
  if (minLoanAmount === '' || minLoanAmount === null || minLoanAmount === undefined) return false;
  if (maxLoanAmount === '' || maxLoanAmount === null || maxLoanAmount === undefined) return false;
  return Number(minLoanAmount) > Number(maxLoanAmount);
};

// Loan Schemes / All Banks
export const LoanSchemes = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showBankDialog, setShowBankDialog] = useState(false);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editingBankId, setEditingBankId] = useState(null);
  const [deleteBankTarget, setDeleteBankTarget] = useState(null);
  const { token, user } = useAuth();
  const basePath = location.pathname.startsWith('/whitelabel') ? '/whitelabel' : '/admin';
  const canManageBanks = user?.role === 'super_admin';

  useEffect(() => {
    if (!token) {
      setBanks([]);
      setError('Unable to load data.');
      return;
    }
    let isMounted = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const resp = await getBanks(token);
        const items = Array.isArray(resp?.data) ? resp.data : [];
        if (!isMounted) return;
        const mapped = items.map((b, idx) => ({
          id: b.id,
          code: `B${String(idx + 1).padStart(3, '0')}`,
          name: b.name,
          type: b.type,
          loanTypes: b.loan_types || [],
          interestRange: b.interest_range || '',
          minLoanAmount: Number(b.min_loan_amount || 0),
          maxLoanAmount: Number(b.max_loan_amount || 0),
          processingFee: b.processing_fee || '',
          status: b.status || 'Active',
        }));
        setBanks(mapped);
      } catch (err) {
        console.error('Failed to load banks', err);
        if (!isMounted) return;
        setBanks([]);
        setError('Unable to load data.');
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [token]);

  const sourceBanks = banks;

  const filteredBanks = sourceBanks.filter((bank) => {
    const matchesSearch = bank.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || bank.type === typeFilter;
    return matchesSearch && matchesType;
  });

  const [form, setForm] = useState({
    name: '',
    type: '',
    interestRange: '',
    minLoanAmount: '',
    maxLoanAmount: '',
    processingFee: '',
    loanTypesText: '',
  });

  const resetForm = () => {
    setForm({
      name: '',
      type: '',
      interestRange: '',
      minLoanAmount: '',
      maxLoanAmount: '',
      processingFee: '',
      loanTypesText: '',
    });
    setEditingBankId(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setShowBankDialog(true);
  };

  const openEditDialog = (bank) => {
    setEditingBankId(bank.id);
    setForm({
      name: bank.name || '',
      type: bank.type === 'NBFC' ? 'nbfc' : 'bank',
      interestRange: bank.interestRange || '',
      minLoanAmount: bank.minLoanAmount || '',
      maxLoanAmount: bank.maxLoanAmount || '',
      processingFee: bank.processingFee || '',
      loanTypesText: (bank.loanTypes || []).join(', '),
    });
    setShowBankDialog(true);
  };

  const openDeleteDialog = (bank) => {
    setDeleteBankTarget(bank);
  };

  const closeDeleteDialog = () => {
    if (deleting) return;
    setDeleteBankTarget(null);
  };

  const handleSubmitBank = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.success(editingBankId ? 'Bank/NBFC updated locally (no API)' : 'Bank/NBFC added locally (no API)');
      setShowBankDialog(false);
      return;
    }
    setSaving(true);
    try {
      if (hasInvalidLoanAmountRange(form.minLoanAmount, form.maxLoanAmount)) {
        throw new Error('Min loan amount must not be greater than max loan amount');
      }
      const loanTypes = form.loanTypesText
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      const payload = {
        name: form.name.trim(),
        type: form.type === 'nbfc' ? 'NBFC' : 'Bank',
        interest_range: form.interestRange.trim() || null,
        min_loan_amount: form.minLoanAmount ? Number(form.minLoanAmount) : null,
        max_loan_amount: form.maxLoanAmount ? Number(form.maxLoanAmount) : null,
        processing_fee: form.processingFee.trim() || null,
        status: 'Active',
        loan_types: loanTypes,
      };
      const resp = editingBankId
        ? await updateBank(token, editingBankId, payload)
        : await createBank(token, payload);
      const b = resp?.data;
      if (b) {
        const mappedBank = {
          id: b.id,
          code: editingBankId
            ? sourceBanks.find((item) => item.id === b.id)?.code || `B${String((sourceBanks?.length || 0) + 1).padStart(3, '0')}`
            : `B${String((sourceBanks?.length || 0) + 1).padStart(3, '0')}`,
          name: b.name,
          type: b.type,
          loanTypes: b.loan_types || [],
          interestRange: b.interest_range || '',
          minLoanAmount: Number(b.min_loan_amount || 0),
          maxLoanAmount: Number(b.max_loan_amount || 0),
          processingFee: b.processing_fee || '',
          status: b.status || 'Active',
        };
        setBanks((prev) => (
          editingBankId
            ? (prev || []).map((item) => (item.id === b.id ? mappedBank : item))
            : [mappedBank, ...(prev || [])]
        ));
      }
      toast.success(editingBankId ? 'Bank/NBFC updated successfully!' : 'Bank/NBFC added successfully!');
      setShowBankDialog(false);
      resetForm();
    } catch (err) {
      toast.error(err?.message || `Failed to ${editingBankId ? 'update' : 'add'} bank`);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBank = async () => {
    if (!token || !deleteBankTarget?.id || !canManageBanks) return;
    setDeleting(true);
    try {
      await deleteBank(token, deleteBankTarget.id);
      setBanks((prev) => (prev || []).filter((item) => item.id !== deleteBankTarget.id));
      toast.success('Bank deleted successfully!');
      setDeleteBankTarget(null);
    } catch (err) {
      toast.error(err?.message || 'Failed to delete bank');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="banks-management">
      <Dialog open={Boolean(deleteBankTarget)} onOpenChange={(open) => { if (!open) closeDeleteDialog(); }}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Delete Bank</DialogTitle>
            <DialogDescription>
              {`Delete "${deleteBankTarget?.name || 'this bank'}"? This action cannot be undone.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeDeleteDialog} disabled={deleting}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={handleDeleteBank} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">
            Banks / NBFC
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage loan schemes and partner banks
          </p>
        </div>
        <Dialog
          open={showBankDialog}
          onOpenChange={(open) => {
            setShowBankDialog(open);
            if (!open) resetForm();
          }}
        >
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-bank-btn" onClick={openCreateDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Add Bank/NBFC
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>{editingBankId ? 'Edit Bank/NBFC' : 'Add Bank/NBFC'}</DialogTitle>
              <DialogDescription>
                {editingBankId ? 'Update the bank details below.' : 'Enter the bank details below.'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmitBank} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Bank Name</Label>
                  <Input
                    placeholder="Enter name"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select
                    value={form.type}
                    onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank">Bank</SelectItem>
                      <SelectItem value="nbfc">NBFC</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Interest Range</Label>
                <Input
                  placeholder="e.g., 8.5% - 14%"
                  value={form.interestRange}
                  onChange={(e) => setForm((f) => ({ ...f, interestRange: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Min Loan Amount</Label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="Enter amount"
                    value={form.minLoanAmount}
                    onChange={(e) => setForm((f) => ({ ...f, minLoanAmount: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Max Loan Amount</Label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="Enter amount"
                    value={form.maxLoanAmount}
                    onChange={(e) => setForm((f) => ({ ...f, maxLoanAmount: e.target.value }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Processing Fee</Label>
                  <Input
                    placeholder="e.g., 0.5% - 1%"
                    value={form.processingFee}
                    onChange={(e) => setForm((f) => ({ ...f, processingFee: e.target.value }))}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Loan Types (comma separated)</Label>
                <Input
                  placeholder="e.g., Home Loan, Personal Loan"
                  value={form.loanTypesText}
                  onChange={(e) => setForm((f) => ({ ...f, loanTypesText: e.target.value }))}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowBankDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" disabled={saving}>
                  {editingBankId ? 'Update Bank' : 'Save Bank'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="border border-border">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search banks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-banks-input"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="Bank">Banks</SelectItem>
                <SelectItem value="NBFC">NBFCs</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border border-border">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Bank Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Loan Types</TableHead>
                  <TableHead>Interest Range</TableHead>
                  <TableHead>Min Amount</TableHead>
                  <TableHead>Max Amount</TableHead>
                  <TableHead>Processing Fee</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-muted-foreground py-6">
                      Loading...
                    </TableCell>
                  </TableRow>
                )}
                {!loading && error && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-muted-foreground py-6">
                      Unable to load data.
                    </TableCell>
                  </TableRow>
                )}
                {!loading && !error && filteredBanks.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-muted-foreground py-6">
                      {sourceBanks.length === 0 ? 'No records available.' : 'No matching records found.'}
                    </TableCell>
                  </TableRow>
                )}
                {!loading && !error && filteredBanks.map((bank) => (
                  <TableRow key={bank.id}>
                    <TableCell className="font-mono text-sm">{bank.id}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{bank.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={typeColors[bank.type]}>{bank.type}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {bank.loanTypes.slice(0, 2).map((type) => (
                          <Badge key={type} variant="outline" className="text-xs">
                            {type}
                          </Badge>
                        ))}
                        {bank.loanTypes.length > 2 && (
                          <Badge variant="outline" className="text-xs">
                            +{bank.loanTypes.length - 2}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{bank.interestRange}</TableCell>
                    <TableCell className="font-medium">
                      {formatCurrency(bank.minLoanAmount)}
                    </TableCell>
                    <TableCell className="font-medium">
                      {formatCurrency(bank.maxLoanAmount)}
                    </TableCell>
                    <TableCell>{bank.processingFee}</TableCell>
                    <TableCell>
                      <Badge className={statusColors[bank.status]}>{bank.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`${basePath}/banks/${bank.id}/view`)}>
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openEditDialog(bank)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          {canManageBanks && (
                            <DropdownMenuItem onClick={() => openDeleteDialog(bank)}>
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const ViewBankPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();
  const [bank, setBank] = useState(null);
  const [loading, setLoading] = useState(true);
  const basePath = location.pathname.startsWith('/whitelabel') ? '/whitelabel' : '/admin';

  useEffect(() => {
    const load = async () => {
      if (!token || !id) return;
      setLoading(true);
      try {
        const resp = await getBank(token, id);
        setBank(resp?.data || null);
      } catch (err) {
        toast.error(err?.message || 'Failed to load bank details');
        navigate(`${basePath}/banks`);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, id, navigate, basePath]);

  if (loading) {
    return <div className="text-sm text-muted-foreground">Loading bank details...</div>;
  }

  if (!bank) return null;

  const loanTypes = Array.isArray(bank.loan_types) ? bank.loan_types : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">Bank Details</h1>
          <p className="text-muted-foreground mt-1">View partner bank and loan scheme details</p>
        </div>
        <Button variant="outline" onClick={() => navigate(`${basePath}/banks`)}>
          Back
        </Button>
      </div>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-muted-foreground" />
            {bank.name}
          </CardTitle>
          <CardDescription>{bank.id}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="rounded-lg border p-4">
              <div className="text-sm text-muted-foreground">Type</div>
              <div className="mt-2"><Badge className={typeColors[bank.type]}>{bank.type}</Badge></div>
            </div>
            <div className="rounded-lg border p-4">
              <div className="text-sm text-muted-foreground">Status</div>
              <div className="mt-2"><Badge className={statusColors[bank.status]}>{bank.status}</Badge></div>
            </div>
            <div className="rounded-lg border p-4">
              <div className="text-sm text-muted-foreground">Interest Range</div>
              <div className="mt-2 font-medium">{bank.interest_range || '—'}</div>
            </div>
            <div className="rounded-lg border p-4">
              <div className="text-sm text-muted-foreground">Min Loan Amount</div>
              <div className="mt-2 font-medium">{bank.min_loan_amount ? formatCurrency(Number(bank.min_loan_amount)) : '—'}</div>
            </div>
            <div className="rounded-lg border p-4">
              <div className="text-sm text-muted-foreground">Max Loan Amount</div>
              <div className="mt-2 font-medium">{bank.max_loan_amount ? formatCurrency(Number(bank.max_loan_amount)) : '—'}</div>
            </div>
            <div className="rounded-lg border p-4">
              <div className="text-sm text-muted-foreground">Processing Fee</div>
              <div className="mt-2 font-medium">{bank.processing_fee || '—'}</div>
            </div>
          </div>

          <div className="rounded-lg border p-4">
            <div className="text-sm text-muted-foreground mb-3">Loan Types</div>
            <div className="flex flex-wrap gap-2">
              {loanTypes.length ? loanTypes.map((type) => (
                <Badge key={type} variant="outline">{type}</Badge>
              )) : <span className="text-sm text-muted-foreground">No loan types configured.</span>}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-lg border p-4">
              <div className="text-sm text-muted-foreground">Created At</div>
              <div className="mt-2 font-medium">{bank.created_at ? new Date(bank.created_at).toLocaleString('en-IN') : '—'}</div>
            </div>
            <div className="rounded-lg border p-4">
              <div className="text-sm text-muted-foreground">Updated At</div>
              <div className="mt-2 font-medium">{bank.updated_at ? new Date(bank.updated_at).toLocaleString('en-IN') : '—'}</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Interest Setup
export const InterestSetup = () => {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [rateForm, setRateForm] = useState({ bankId: '', loanType: '', base: '', max: '' });
  const { token } = useAuth();

  useEffect(() => {
    if (!token) return;
    let isMounted = true;
    (async () => {
      setLoading(true);
      try {
        const resp = await getBanks(token);
        const items = Array.isArray(resp?.data) ? resp.data : [];
        if (!isMounted) return;
        const mapped = items.map((b) => ({
          id: b.id,
          name: b.name,
          loanTypes: b.loan_types || [],
          interestRange: b.interest_range || '',
        }));
        setBanks(mapped);
      } catch (err) {
        console.error('Failed to load banks for interest setup', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [token]);

  const rows = banks.flatMap((bank) =>
    (bank.loanTypes.length ? bank.loanTypes : ['General']).map((loanType, idx) => ({
      key: `${bank.id}-${idx}-${loanType}`,
      bank,
      loanType,
    })),
  );

  const openEdit = (bank, loanType) => {
    const [base = '', max = ''] = (bank.interestRange || '').split('-').map((s) => s.trim());
    setRateForm({
      bankId: bank.id,
      loanType,
      base: base.replace('%', ''),
      max: max.replace('%', ''),
    });
    setEditOpen(true);
  };

  const handleSaveRate = async (e) => {
    e.preventDefault();
    if (!token || !rateForm.bankId) {
      setEditOpen(false);
      return;
    }
    try {
      const baseVal = rateForm.base.trim();
      const maxVal = rateForm.max.trim();
      const interest_range =
        baseVal && maxVal ? `${baseVal}% - ${maxVal}%` : baseVal ? `${baseVal}%` : '';
      await updateBank(token, rateForm.bankId, { interest_range });
      setBanks((prev) =>
        prev.map((b) =>
          b.id === rateForm.bankId
            ? { ...b, interestRange: interest_range }
            : b,
        ),
      );
      toast.success('Interest rate updated');
      setEditOpen(false);
    } catch (err) {
      toast.error(err?.message || 'Failed to update interest rate');
    }
  };

  return (
    <div className="space-y-6" data-testid="interest-setup">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">
          Interest Setup
        </h1>
        <p className="text-muted-foreground mt-1">Configure interest rates by bank and loan type</p>
      </div>

      <Card className="border border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Bank</TableHead>
                <TableHead>Loan Type</TableHead>
                <TableHead>Base Rate</TableHead>
                <TableHead>Max Rate</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                    Loading...
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                rows.map(({ key, bank, loanType }) => {
                  const [base = '', max = ''] = (bank.interestRange || '')
                    .split('-')
                    .map((s) => s.trim());
                  return (
                    <TableRow key={key}>
                      <TableCell className="font-medium">{bank.name}</TableCell>
                      <TableCell>{loanType}</TableCell>
                      <TableCell>{base}</TableCell>
                      <TableCell>{max}</TableCell>
                      <TableCell>
                        <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(bank, loanType)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Edit Interest Rate</DialogTitle>
            <DialogDescription>
              Set base and max interest rate for <span className="font-semibold">{rateForm.loanType}</span>.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveRate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Base Rate (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={rateForm.base}
                  onChange={(e) => setRateForm((f) => ({ ...f, base: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label>Max Rate (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={rateForm.max}
                  onChange={(e) => setRateForm((f) => ({ ...f, max: e.target.value }))}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Processing Fees
export const ProcessingFees = () => {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [feeForm, setFeeForm] = useState({
    bankId: '',
    processingFee: '',
    minLoanAmount: '',
    maxLoanAmount: '',
  });
  const { token } = useAuth();

  useEffect(() => {
    if (!token) return;
    let isMounted = true;
    (async () => {
      setLoading(true);
      try {
        const resp = await getBanks(token);
        const items = Array.isArray(resp?.data) ? resp.data : [];
        if (!isMounted) return;
        const mapped = items.map((b) => ({
          id: b.id,
          name: b.name,
          type: b.type,
          processingFee: b.processing_fee || '',
          minLoanAmount: Number(b.min_loan_amount || 0),
          maxLoanAmount: Number(b.max_loan_amount || 0),
          status: b.status || 'Active',
        }));
        setBanks(mapped);
      } catch (err) {
        console.error('Failed to load banks for processing fees', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [token]);

  const openEdit = (bank) => {
    setFeeForm({
      bankId: bank.id,
      processingFee: bank.processingFee.replace(/\s/g, ''),
      minLoanAmount: bank.minLoanAmount ? String(bank.minLoanAmount) : '',
      maxLoanAmount: bank.maxLoanAmount ? String(bank.maxLoanAmount) : '',
    });
    setEditOpen(true);
  };

  const handleSaveFee = async (e) => {
    e.preventDefault();
    if (!token || !feeForm.bankId) {
      setEditOpen(false);
      return;
    }
    try {
      if (hasInvalidLoanAmountRange(feeForm.minLoanAmount, feeForm.maxLoanAmount)) {
        throw new Error('Min loan amount must not be greater than max loan amount');
      }
      const min_loan_amount = feeForm.minLoanAmount ? Number(feeForm.minLoanAmount) : null;
      const max_loan_amount = feeForm.maxLoanAmount ? Number(feeForm.maxLoanAmount) : null;
      const processing_fee = feeForm.processingFee.trim() || null;
      await updateBank(token, feeForm.bankId, {
        min_loan_amount,
        max_loan_amount,
        processing_fee,
      });
      setBanks((prev) =>
        prev.map((b) =>
          b.id === feeForm.bankId
            ? {
                ...b,
                minLoanAmount: min_loan_amount || 0,
                maxLoanAmount: max_loan_amount || 0,
                processingFee: processing_fee || '',
              }
            : b,
        ),
      );
      toast.success('Processing fee updated');
      setEditOpen(false);
    } catch (err) {
      toast.error(err?.message || 'Failed to update processing fee');
    }
  };

  return (
    <div className="space-y-6" data-testid="processing-fees">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">
          Processing Fees
        </h1>
        <p className="text-muted-foreground mt-1">Configure processing fees by bank</p>
      </div>

      <Card className="border border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Bank</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Processing Fee</TableHead>
                <TableHead>Max Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                    Loading...
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                banks.map((bank) => (
                  <TableRow key={bank.id}>
                    <TableCell className="font-medium">{bank.name}</TableCell>
                    <TableCell>
                      <Badge className={typeColors[bank.type]}>{bank.type}</Badge>
                    </TableCell>
                    <TableCell className="font-medium">
                      {bank.processingFee || '—'}
                    </TableCell>
                    <TableCell>
                      {bank.maxLoanAmount ? formatCurrency(bank.maxLoanAmount) : '—'}
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[bank.status]}>{bank.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(bank)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Edit Processing Fee</DialogTitle>
            <DialogDescription>
              Update processing fee and max loan amount for this bank.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveFee} className="space-y-4">
            <div className="space-y-1">
              <Label>Processing Fee</Label>
              <Input
                placeholder="e.g., 0.5% - 1%"
                value={feeForm.processingFee}
                onChange={(e) => setFeeForm((f) => ({ ...f, processingFee: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Min Loan Amount</Label>
                <Input
                  type="number"
                  min="0"
                  placeholder="Enter amount"
                  value={feeForm.minLoanAmount}
                  onChange={(e) => setFeeForm((f) => ({ ...f, minLoanAmount: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label>Max Loan Amount</Label>
                <Input
                  type="number"
                  min="0"
                  placeholder="Enter amount"
                  value={feeForm.maxLoanAmount}
                  onChange={(e) => setFeeForm((f) => ({ ...f, maxLoanAmount: e.target.value }))}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Integration Settings
export const IntegrationSettings = () => {
  return (
    <div className="space-y-6" data-testid="integration-settings">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">
          Integration Settings
        </h1>
        <p className="text-muted-foreground mt-1">Manage bank API integrations</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {mockBanks.slice(0, 4).map((bank) => (
          <Card key={bank.id} className="border border-border">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-slate-100 dark:bg-slate-800">
                    <Building2 className="h-5 w-5" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{bank.name}</CardTitle>
                    <CardDescription>{bank.type}</CardDescription>
                  </div>
                </div>
                <Badge className={statusColors[bank.status]}>{bank.status}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">API Status</span>
                  <span className="text-emerald-600 font-medium">Connected</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Last Sync</span>
                  <span>2 hours ago</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Webhook</span>
                  <span className="text-emerald-600 font-medium">Active</span>
                </div>
              </div>
              <Button variant="outline" className="w-full mt-4">
                <Settings className="h-4 w-4 mr-2" />
                Configure
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default LoanSchemes;
